import React, { useState } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Notiflix from "notiflix";

const API_BASE = process.env.REACT_APP_MEMBERSHIP_API || "http://localhost:5001";

const benefits = [
  {
    title: "Member Support",
    desc: "Priority access to our IT help desk for all of your technology questions.",
    icon: "M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z",
  },
  {
    title: "Exclusive Resources",
    desc: "Guides, tutorials and tools to keep your devices and business secure.",
    icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.247m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.247",
  },
  {
    title: "Community Access",
    desc: "Connect with other members, share knowledge and grow together.",
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
  },
  {
    title: "100% Free, Forever",
    desc: "Membership will always be free — part of our non-profit mission.",
    icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
];

const Membership = () => {
  useDocTitle("ZelT | Membership");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justVerified = searchParams.get("verified") === "1";
  const verifyFailed = searchParams.get("verified") === "0";
  const [mode, setMode] = useState("signup");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referral, setReferral] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [showPending, setShowPending] = useState(false);
  const [resending, setResending] = useState(false);

  const switchMode = (m) => {
    setMode(m);
    setUsername("");
    setEmail("");
    setPassword("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const cleanUsername = username.trim();
    const cleanEmail = email.trim();

    const url = mode === "signup" ? `${API_BASE}/register` : `${API_BASE}/login`;
    const data =
      mode === "signup"
        ? { username: cleanUsername, email: cleanEmail, password, referral }
        : { email: cleanEmail, password };

    axios
      .post(url, data)
      .then((response) => {
        // signup now requires email verification before login
        if (response.data.pendingVerification) {
          setPendingEmail(cleanEmail);
          setShowPending(true);
          Notiflix.Notify.info("📧 Check your email to activate your account.");
          return;
        }
        localStorage.setItem("zelt_token", response.data.token);
        localStorage.setItem("zelt_member", JSON.stringify(response.data.member));
        Notiflix.Report.success(
          "Welcome!",
          "You are now logged in.",
          "Continue"
        );
        navigate("/members");
      })
      .catch((error) => {
        const data = error.response?.data || {};
        if (data.needsVerification) {
          setPendingEmail(cleanEmail);
          setShowPending(true);
          Notiflix.Notify.warning("📧 Verify your email first — see the resend option below.");
          return;
        }
        const message = data.message || "Something went wrong. Please try again.";
        Notiflix.Report.failure("Membership", message, "Okay");
      })
      .finally(() => setLoading(false));
  };

  const resendVerification = () => {
    if (!pendingEmail.trim()) return;
    setResending(true);
    axios
      .post(`${API_BASE}/resend-verification`, { email: pendingEmail.trim() })
      .then(() =>
        Notiflix.Notify.success("📧 If verification is needed, a new email has been sent.")
      )
      .catch(() => Notiflix.Notify.failure("Too many requests — try again later."))
      .finally(() => setResending(false));
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent text-gray-700";

  return (
    <div>
      <NavBar />
      <div id="membership" className="mt-8 w-full bg-white py-12 lg:py-24">
        <div className="flex flex-col lg:flex-row justify-between items-center mx-4 lg:mx-20 gap-12" data-aos="zoom-in">
          <div className="lg:w-1/2 text-center lg:text-left">
            <h1 className="mb-5 md:text-5xl text-3xl font-bold text-blue-900">
              Become a <b>ZelTechnology Member</b> — for FREE.
            </h1>
            <div className="text-xl font-semibold tracking-tight mb-8 text-gray-500">
              Join our community and unlock member-only benefits. Membership is
              completely free and always will be.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {benefits.map((b) => (
                <div key={b.title} className="flex flex-col items-center sm:items-start">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-900 text-white mb-3 shadow-lg">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={b.icon} />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg text-blue-900">{b.title}</h3>
                  <p className="text-gray-500">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:w-5/12 w-full max-w-md" data-aos="fade-up">
            {/* verification result banners (from email link redirect) */}
            {justVerified && (
              <div className="mb-5 bg-green-50 border-2 border-green-400 rounded-2xl p-5 text-center">
                <p className="text-3xl mb-1">✅</p>
                <p className="font-extrabold text-green-700">
                  Account verified!
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  Log in below — welcome to ZelTechnology! 🦈
                </p>
              </div>
            )}
            {verifyFailed && (
              <div className="mb-5 bg-red-50 border-2 border-red-300 rounded-2xl p-5 text-center">
                <p className="text-3xl mb-1">⚠️</p>
                <p className="font-extrabold text-red-700">
                  Verification link expired or invalid
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  Log in with your email to resend the verification email — or
                  sign up again.
                </p>
              </div>
            )}

            <div className="bg-black p-8 rounded-2xl shadow-2xl">
              <div className="bg-white rounded-xl p-6">
                <div className="flex mb-6 rounded-xl bg-gray-100 p-1">
                  <button
                    onClick={() => switchMode("signup")}
                    className={`w-1/2 py-2 rounded-lg font-bold transition duration-200 ${mode === "signup" ? "bg-blue-900 text-white shadow" : "text-gray-500 hover:text-blue-900"}`}
                  >
                    Sign Up
                  </button>
                  <button
                    onClick={() => switchMode("login")}
                    className={`w-1/2 py-2 rounded-lg font-bold transition duration-200 ${mode === "login" ? "bg-blue-900 text-white shadow" : "text-gray-500 hover:text-blue-900"}`}
                  >
                    Login
                  </button>
                </div>

                <h2 className="font-bold text-center text-blue-900 uppercase text-2xl mb-6">
                  {showPending
                    ? "📧 Almost there!"
                    : mode === "signup"
                    ? "Create your free account"
                    : "Welcome back"}
                </h2>

                {showPending ? (
                  /* verification pending panel */
                  <div className="text-center">
                    <p className="text-5xl mb-4">📧</p>
                    <p className="text-gray-700 mb-2 font-semibold">
                      Check your email!
                    </p>
                    <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                      We sent a verification link to{" "}
                      <b className="text-blue-900">{pendingEmail}</b>. Click it
                      within 24 hours to activate your free membership, then
                      log in.
                    </p>
                    <p className="text-gray-500 text-sm mb-2">
                      Didn't get it? Check spam, or:
                    </p>
                    <button
                      type="button"
                      onClick={resendVerification}
                      disabled={resending}
                      className="w-full py-2.5 rounded-xl bg-blue-900 text-white text-sm font-bold hover:bg-blue-800 disabled:opacity-50 mb-2"
                    >
                      {resending ? "Sending…" : "Resend verification email"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPending(false)}
                      className="text-blue-900 font-bold text-sm hover:underline"
                    >
                      ← Back
                    </button>
                  </div>
                ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4" autoComplete="on">
                  {mode === "signup" && (
                    <input
                      type="text"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className={inputClass}
                      maxLength={20}
                      pattern="[a-zA-Z0-9_]{3,20}"
                      title="3-20 characters: letters, numbers, or underscores"
                      autoComplete="username"
                      required
                    />
                  )}
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    maxLength={254}
                    autoComplete="email"
                    required
                  />
                  <input
                    type="password"
                    placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass}
                    maxLength={64}
                    minLength={mode === "signup" ? 8 : undefined}
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    required
                  />
                  {mode === "signup" && (
                    <select
                      value={referral}
                      onChange={(e) => setReferral(e.target.value)}
                      className={inputClass}
                      aria-label="How did you hear about us?"
                    >
                      <option value="">How did you hear about us? (optional)</option>
                      <option value="Google search">Google search</option>
                      <option value="Nextdoor">Nextdoor</option>
                      <option value="Facebook">Facebook</option>
                      <option value="Friend or family">Friend or family</option>
                      <option value="Veteran organization">Veteran organization</option>
                      <option value="Church or charity">Church or charity</option>
                      <option value="Community event">Community event</option>
                      <option value="Other">Other</option>
                    </select>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="text-white bg-blue-900 hover:bg-blue-800 disabled:opacity-50 inline-flex items-center justify-center w-full px-6 py-3 text-lg shadow-xl rounded-2xl"
                  >
                    {loading ? "Please wait..." : mode === "signup" ? "Join for FREE" : "Login"}
                  </button>
                </form>
                )}

                <p className="text-center text-gray-500 text-sm mt-4">
                  {mode === "signup" ? (
                    <>Already a member?{" "}
                      <button type="button" onClick={() => switchMode("login")} className="text-blue-900 font-bold hover:underline">
                        Login here
                      </button>
                    </>
                  ) : (
                    <>Not a member yet?{" "}
                      <button type="button" onClick={() => switchMode("signup")} className="text-blue-900 font-bold hover:underline">
                        Sign up free
                      </button>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Membership;
